import React, { useState, useEffect, useCallback } from 'react';
import { dashboardBannerAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Loader2, Eye, EyeOff, Image as ImageIcon, X,
  Clock, GalleryHorizontalEnd,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TZ_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern (FL / New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
];

const INTERNAL_PRESETS = [
  { value: '/calendar', label: 'Calendar' },
  { value: '/academy', label: 'Academy' },
  { value: '/knowledge', label: 'Knowledge' },
  { value: '/announcements', label: 'Announcements' },
  { value: '/dashboard', label: 'Dashboard' },
  { value: '/my-contracts', label: 'My Contracts' },
  { value: '__custom__', label: 'Custom path…' },
];

const emptyForm = {
  name: '', type: 'image', active: true, sort_order: 0,
  button_enabled: false, button_label: '', button_link_type: 'internal', button_link_value: '',
  label: '', target_local: '', target_tz: 'America/New_York', arrival_label: '',
};

export default function DashboardBannerManager() {
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pendingImages, setPendingImages] = useState({});   // { slot: File } for create mode
  const [busy, setBusy] = useState(false);
  const [customInternal, setCustomInternal] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await dashboardBannerAPI.list();
      setBanners(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setPendingImages({});
    setCustomInternal(false);
    setShowDialog(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || '', type: b.type || 'image', active: !!b.active, sort_order: b.sort_order || 0,
      button_enabled: !!b.button_enabled, button_label: b.button_label || '',
      button_link_type: b.button_link_type || 'internal', button_link_value: b.button_link_value || '',
      label: b.label || '', target_local: b.target_local || '',
      target_tz: b.target_tz || 'America/New_York', arrival_label: b.arrival_label || '',
    });
    const isPreset = INTERNAL_PRESETS.some((p) => p.value === b.button_link_value);
    setCustomInternal(b.button_link_type === 'internal' && !!b.button_link_value && !isPreset);
    setPendingImages({});
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.type === 'countdown' && !form.target_local) {
      toast.error('Countdown banners need a target date & time'); return;
    }
    setBusy(true);
    try {
      let saved;
      if (editing) {
        const r = await dashboardBannerAPI.update(editing.banner_id, form);
        saved = r.data;
      } else {
        const r = await dashboardBannerAPI.create(form);
        saved = r.data;
      }
      // Upload any pending images (create mode defers until the id exists).
      const slots = Object.keys(pendingImages);
      for (const slot of slots) {
        try {
          await dashboardBannerAPI.uploadImage(saved.banner_id, slot, pendingImages[slot]);
        } catch (e) {
          toast.error(`Image (${slot}) upload failed — retry from Edit.`);
        }
      }
      toast.success(editing ? 'Banner updated' : 'Banner created');
      setShowDialog(false);
      setPendingImages({});
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const validateImage = (file) => {
    if (!/^image\/(png|jpeg|webp|gif)$/.test(file.type)) {
      toast.error('Image must be PNG, JPEG, WebP or GIF'); return false;
    }
    if (file.size > 300 * 1024 * 1024) { toast.error('Image must be ≤ 300 MB'); return false; }
    return true;
  };

  const pickImage = async (slot, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !validateImage(file)) return;
    if (editing) {
      try {
        setBusy(true);
        const r = await dashboardBannerAPI.uploadImage(editing.banner_id, slot, file);
        setEditing(r.data);
        toast.success('Image uploaded');
        load();
      } catch (err) {
        toast.error(getErrorMessage(err, 'Upload failed'));
      } finally { setBusy(false); }
    } else {
      setPendingImages((p) => ({ ...p, [slot]: file }));
    }
  };

  const removeImage = async (slot) => {
    if (editing && editing[`${slot}_image_url`]) {
      try {
        const r = await dashboardBannerAPI.deleteImage(editing.banner_id, slot);
        setEditing(r.data);
        toast.success('Image removed');
        load();
      } catch (err) { toast.error(getErrorMessage(err)); }
    } else {
      setPendingImages((p) => { const n = { ...p }; delete n[slot]; return n; });
    }
  };

  const toggleActive = async (b) => {
    try {
      await dashboardBannerAPI.update(b.banner_id, { active: !b.active });
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const handleDelete = async () => {
    try {
      await dashboardBannerAPI.delete(deleteTarget.banner_id);
      toast.success('Banner deleted');
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const slotPreview = (slot) => {
    if (pendingImages[slot]) return URL.createObjectURL(pendingImages[slot]);
    if (editing && editing[`${slot}_image_url`]) return `${BACKEND}${editing[`${slot}_image_url`]}`;
    return null;
  };

  const ImageSlot = ({ slot, label, hint }) => {
    const preview = slotPreview(slot);
    return (
      <div className="space-y-1.5">
        <Label className="text-xs">{label}</Label>
        {preview ? (
          <div className="relative rounded-lg border overflow-hidden bg-slate-50">
            <img src={preview} alt={label} className="w-full h-auto block max-h-40 object-contain mx-auto" />
            <button type="button" onClick={() => removeImage(slot)}
              className="absolute top-2 right-2 bg-white/90 rounded-full p-1 hover:bg-white shadow"
              data-testid={`banner-img-remove-${slot}`}>
              <X size={14} />
            </button>
          </div>
        ) : (
          <label className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg p-3 hover:border-slate-400 cursor-pointer transition-colors">
            <ImageIcon size={18} className="text-slate-400" />
            <span className="text-[11px] text-slate-600">{hint}</span>
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="sr-only"
              onChange={(e) => pickImage(slot, e)} disabled={busy}
              data-testid={`banner-img-input-${slot}`} />
          </label>
        )}
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" size={28} /></div>;

  return (
    <div data-testid="dashboard-banner-manager" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Full-width banners that rotate every 8s on every dashboard. Recommended art:
          very wide format (e.g. <b>7968×200 px</b>). Image banners keep their aspect ratio edge-to-edge.
        </p>
        <Button onClick={openCreate} data-testid="create-banner-btn">
          <Plus size={16} className="mr-2" /> New Banner
        </Button>
      </div>

      {banners.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-slate-500">
          <GalleryHorizontalEnd size={32} className="mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-700">No banners yet</p>
          <p>Create a banner to feature it on the dashboards.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {banners.map((b) => {
            const thumb = b.type === 'image'
              ? b.main_image_url
              : (b.countdown_image_url || b.arrival_image_url);
            return (
              <Card key={b.banner_id} className={`overflow-hidden ${b.active ? '' : 'opacity-60'}`} data-testid={`banner-row-${b.banner_id}`}>
                <CardContent className="py-3 flex items-center gap-4">
                  <div className="w-40 shrink-0 rounded-md overflow-hidden bg-slate-100 border">
                    {thumb ? (
                      <img src={`${BACKEND}${thumb}`} alt={b.name} className="w-full h-auto block max-h-16 object-cover" />
                    ) : (
                      <div className="h-12 flex items-center justify-center text-[10px] text-slate-400">No image</div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">{b.name}</span>
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {b.type === 'countdown' ? <Clock size={10} className="mr-1" /> : null}{b.type}
                      </Badge>
                      <Badge variant={b.active ? 'default' : 'secondary'} className="text-[10px]">{b.active ? 'Active' : 'Inactive'}</Badge>
                    </div>
                    {b.type === 'countdown' && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        {b.label ? `${b.label} · ` : ''}Target: {b.target_local || '—'} ({b.target_tz})
                      </p>
                    )}
                    {b.button_enabled && b.button_label && (
                      <p className="text-[11px] text-slate-400 mt-0.5">Button: {b.button_label} → {b.button_link_value}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => toggleActive(b)} data-testid={`toggle-banner-${b.banner_id}`}>
                      {b.active ? <EyeOff size={14} /> : <Eye size={14} />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`edit-banner-${b.banner_id}`}><Edit size={14} /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteTarget(b)} data-testid={`delete-banner-${b.banner_id}`}><Trash2 size={14} /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit' : 'New'} Banner</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => upd('name', e.target.value)} placeholder="e.g. The Escape — Coming Soon" data-testid="banner-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => upd('type', v)}>
                  <SelectTrigger data-testid="banner-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="countdown">Countdown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => upd('sort_order', Number(e.target.value) || 0)} data-testid="banner-order-input" />
              </div>
            </div>

            {form.type === 'image' && (
              <ImageSlot slot="main" label="Banner art (full-width)"
                hint="Upload art · recommended ~7968×200 px · PNG/JPEG/WebP/GIF ≤ 300 MB" />
            )}

            {form.type === 'countdown' && (
              <>
                <div className="space-y-1.5">
                  <Label>Countdown label</Label>
                  <Input value={form.label} onChange={(e) => upd('label', e.target.value)} placeholder="e.g. Start OEP" data-testid="banner-label-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Target date & time</Label>
                    <Input type="datetime-local" value={form.target_local} onChange={(e) => upd('target_local', e.target.value)} data-testid="banner-target-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Timezone</Label>
                    <Select value={form.target_tz} onValueChange={(v) => upd('target_tz', v)}>
                      <SelectTrigger data-testid="banner-tz-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TZ_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 -mt-2">Shows days only when &gt; 45 days out; switches to days/hours/min/sec inside 45 days.</p>
                <ImageSlot slot="countdown" label="Background art — during countdown"
                  hint="Background behind the countdown · ~7968×200 px ≤ 300 MB" />
                <ImageSlot slot="arrival" label="Arrival art — shown when it hits zero"
                  hint="Swaps in at 0 · ~7968×200 px ≤ 300 MB" />
                <div className="space-y-1.5">
                  <Label>Arrival text (fallback if no arrival image)</Label>
                  <Input value={form.arrival_label} onChange={(e) => upd('arrival_label', e.target.value)} placeholder="e.g. It's here!" data-testid="banner-arrival-label-input" />
                </div>
              </>
            )}

            {/* CTA button */}
            <div className="rounded-lg border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label>Call-to-action button</Label>
                <Switch checked={form.button_enabled} onCheckedChange={(v) => upd('button_enabled', v)} data-testid="banner-button-switch" />
              </div>
              {form.button_enabled && (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Button label</Label>
                    <Input value={form.button_label} onChange={(e) => upd('button_label', e.target.value)} placeholder="e.g. Learn more" data-testid="banner-button-label-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Link type</Label>
                      <Select value={form.button_link_type} onValueChange={(v) => { upd('button_link_type', v); upd('button_link_value', ''); setCustomInternal(false); }}>
                        <SelectTrigger data-testid="banner-link-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="internal">Internal page</SelectItem>
                          <SelectItem value="external">External URL</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Destination</Label>
                      {form.button_link_type === 'internal' ? (
                        <Select
                          value={customInternal ? '__custom__' : form.button_link_value}
                          onValueChange={(v) => {
                            if (v === '__custom__') { setCustomInternal(true); upd('button_link_value', ''); }
                            else { setCustomInternal(false); upd('button_link_value', v); }
                          }}>
                          <SelectTrigger data-testid="banner-internal-select"><SelectValue placeholder="Select page" /></SelectTrigger>
                          <SelectContent>
                            {INTERNAL_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input value={form.button_link_value} onChange={(e) => upd('button_link_value', e.target.value)} placeholder="https://…" data-testid="banner-external-input" />
                      )}
                    </div>
                  </div>
                  {form.button_link_type === 'internal' && customInternal && (
                    <Input value={form.button_link_value} onChange={(e) => upd('button_link_value', e.target.value)} placeholder="/custom/path" data-testid="banner-internal-custom-input" />
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Active (visible on dashboards)</Label>
              <Switch checked={form.active} onCheckedChange={(v) => upd('active', v)} data-testid="banner-active-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={busy} data-testid="banner-save-btn">
              {busy ? <Loader2 size={15} className="animate-spin mr-1" /> : null}{editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Banner</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete &ldquo;{deleteTarget?.name}&rdquo;.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
